import { Component, ChangeDetectionStrategy, computed, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Bucket, GroupPage, GroupSpendingTrendPoint } from '../../types';
import { DrawerService } from '../../services/drawer.service';
import { MonthService } from '../../services/month.service';
import { ChartService } from '../../services/chart.service';
import { SessionService } from '../../services/session.service';
import { ChartDataInput } from 'src/app/shared/doughnut-chart/doughnut-chart.component';
import { Observable } from 'rxjs';
import { AsyncPipe, CurrencyPipe } from '@angular/common';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { UiActionRowComponent } from '../../ui/action-row/action-row.component';
import { SpendTrendChartComponent } from '../../shared/spend-trend-chart/spend-trend-chart.component';
import { UpcomingRecurringCardsComponent } from '../upcoming-recurring-cards/upcoming-recurring-cards.component';
import { isOverBudgetBucket } from '../bucket-over-budget';
import { budgetVarianceDigits } from '../budget-variance-format';
import { TransactionRuleSuggestionLookupService } from '../../transaction-rules/transaction-rule-suggestion-lookup.service';

@Component({
  selector: 'app-categories-page-summary',
  templateUrl: './categories-page-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiButtonDirective,
    UiIconComponent,
    UiActionRowComponent,
    SpendTrendChartComponent,
    UpcomingRecurringCardsComponent,
    AsyncPipe,
    CurrencyPipe,
  ],
})
export class CategoriesPageSummaryComponent {
  drawerService = inject(DrawerService);
  month = inject(MonthService);
  private chartService = inject(ChartService);
  sessionService = inject(SessionService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly groupPage = input.required<GroupPage>();
  readonly spendingTrend = input<GroupSpendingTrendPoint[]>([]);

  categoryChartData: ChartDataInput[] = [];
  unsortedTransactionsCount$: Observable<number>;

  get spent(): number {
    return this.round(this.groupPage().balance.BUDGET * -1);
  }

  get leftThisMonth(): number {
    return this.round(this.groupPage().balance.INCOME - this.spent);
  }

  private round(num: number): number {
    return Math.round(num * 100) / 100;
  }

  get overBudget(): number {
    const groupPage = this.groupPage();
    return this.round(groupPage.budget - groupPage.budgeted.INCOME);
  }

  get leftToBudget(): number {
    const page = this.groupPage();
    return this.round((page.budgeted?.INCOME ?? 0) - (page.budget ?? 0));
  }

  get hasLeftToBudget(): boolean {
    return this.leftToBudget > 0;
  }

  get isOverassigned(): boolean {
    return this.leftToBudget <= -1;
  }

  get overassignedAmount(): number {
    return Math.abs(this.leftToBudget);
  }

  get overassignedAmountDigits(): string {
    return budgetVarianceDigits(this.overassignedAmount);
  }

  get leftToBudgetDigits(): string {
    return budgetVarianceDigits(this.leftToBudget);
  }

  readonly unbudgetedBalance = computed(() => {
    return this.round(this.groupPage().unbudgeted?.unbudgeted ?? 0);
  });

  readonly hasUnbudgetedBalance = computed(() => this.unbudgetedBalance() !== 0);

  readonly unbudgetedBalanceAmount = computed(() => Math.abs(this.unbudgetedBalance()));

  readonly unbudgetedBalanceAmountDigits = computed(() => (Number.isInteger(this.unbudgetedBalanceAmount()) ? 0 : 2));

  readonly unbudgetedBalanceTitle = computed(() => {
    const amount = this.formatCurrency(this.unbudgetedBalanceAmount(), this.unbudgetedBalanceAmountDigits());
    if (this.unbudgetedBalance() > 0) return `Transfer ${amount}`;
    return `Fix ${amount} shortfall`;
  });

  readonly unbudgetedBalanceSubtitle = computed(() =>
    this.unbudgetedBalance() > 0 ? 'Available balance needs a budget' : 'Budgeted dollars exceed account balance',
  );

  get overBudgetBuckets(): Bucket[] {
    return (
      this.groupPage()
        .data.flatMap(category => category.buckets)
        // This card has always listed spending buckets only; the shared helper also
        // covers SAVINGS, so keep the BUDGET narrowing and inherit its over-budget rule.
        .filter(bucket => bucket.type === 'BUDGET' && isOverBudgetBucket(bucket))
        .sort((left, right) => this.bucketRemaining(left) - this.bucketRemaining(right))
    );
  }

  get topOverBudgetBucket(): Bucket | null {
    return this.overBudgetBuckets[0] ?? null;
  }

  private readonly ruleSuggestions = inject(TransactionRuleSuggestionLookupService);
  /** With Reports on, the chart chip opens Reports for the month in view instead of the old chart drawer. */
  readonly reportsEnabled = toSignal(this.sessionService.hasEnabledFeature$('reports'), { initialValue: false });
  readonly suggestedRuleCount = this.ruleSuggestions.activeSuggestionCount;

  readonly attentionCount = computed(() => {
    return (
      (this.overBudgetBuckets.length ? 1 : 0) +
      (this.hasLeftToBudget || this.isOverassigned ? 1 : 0) +
      (this.hasUnbudgetedBalance() ? 1 : 0) +
      (this.suggestedRuleCount() > 0 ? 1 : 0)
    );
  });

  get spendingPaceLabel(): string {
    const plannedPaceSpend = this.latestActualTrendPoint()?.plannedCumulativeSpend ?? 0;
    if (!plannedPaceSpend) return 'Current month activity';
    const delta = Math.abs(this.spent - plannedPaceSpend);
    return `${this.spent <= plannedPaceSpend ? 'Under' : 'Over'} planned pace by ${this.formatCurrency(delta, 0)}`;
  }

  constructor() {
    this.unsortedTransactionsCount$ = this.sessionService.unsortedTransactionsCount$;

    effect(() => {
      this.categoryChartData = this.chartService.groupChartExpected(this.groupPage());
    });
  }

  openSpendingChart(): void {
    if (!this.reportsEnabled()) {
      this.drawerService.openGroupsPieChartDrawer();
      return;
    }
    // Reports never shows a future month, so a budget planned ahead opens on this month.
    const thisMonth = this.month.monthYearFromDate();
    const viewed = this.month.currentMonthYear() || thisMonth;
    const monthYear = viewed > thisMonth ? thisMonth : viewed;
    void this.router.navigate(['/reports'], { queryParams: { from: monthYear, to: monthYear } });
  }

  openOverBudget(): void {
    const bucket = this.topOverBudgetBucket;
    if (bucket) this.drawerService.openBucketDrawer(bucket.id);
  }

  openUnbudgetedBalance(): void {
    this.drawerService.openUnbudgetedDrawer();
  }

  openAssignMoney(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: 'budgets' },
      queryParamsHandling: 'merge',
    });
  }

  openSuggestedRules(): void {
    void this.router.navigate(['/transaction-rules'], { state: { returnTo: 'budgets' } });
  }

  openUncategorizedTransactions(): void {
    void this.router.navigate(['/transactions'], { queryParams: { uncategorized: '1' } });
  }

  bucketRemaining(bucket: Bucket): number {
    const fundBalance = bucket.fund?.balance;
    if (fundBalance !== undefined) return this.round(fundBalance);
    return this.round(bucket.amount + bucket.total);
  }

  formatCurrency(value: number, digits = 0): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  }

  private latestActualTrendPoint() {
    const trend = this.spendingTrend();

    for (let index = trend.length - 1; index >= 0; index -= 1)
      if (trend[index]?.cumulativeSpend !== null) return trend[index];

    return null;
  }
}
